import type { ReactNode } from 'react';
import {
  formatMoney,
  PIPELINE_STAGES,
  STATUS_META,
  type DealStatus,
  type StatusMeta,
} from '@accel/shared';

/**
 * Money always arrives as a minor-unit string. Parsing it with BigInt rather
 * than Number is what keeps a UGX or IDR balance from silently rounding at
 * the display layer.
 */
export function Money({
  minor,
  currency,
  compact = false,
}: {
  minor: string | bigint | null | undefined;
  currency: string;
  compact?: boolean;
}) {
  if (minor === null || minor === undefined) return <span className="num">—</span>;
  const value = typeof minor === 'bigint' ? minor : BigInt(minor);
  return <span className="num">{formatMoney(value, currency, { compact })}</span>;
}

export function StatusTag({ status }: { status: DealStatus }) {
  const meta: StatusMeta = STATUS_META[status];
  return <span className={`tag ${meta.tone}`}>{meta.label}</span>;
}

export function Tag({ tone, children }: { tone: StatusMeta['tone']; children: ReactNode }) {
  return <span className={`tag ${tone}`}>{children}</span>;
}

export function Avatar({ name, colour }: { name: string; colour?: string | null }) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span className="avatar" style={{ background: colour ?? 'var(--slate)' }}>
      {initials}
    </span>
  );
}

export function PartyCell({
  party,
  sub,
}: {
  party: { name: string; code: string; colour: string | null };
  sub?: string;
}) {
  return (
    <span className="party">
      <Avatar name={party.name} colour={party.colour} />
      <span>
        <span className="cell-t1" style={{ display: 'block' }}>
          {party.name}
        </span>
        <span className="cell-t2">{sub ?? party.code}</span>
      </span>
    </span>
  );
}

export function Kpi({
  label,
  value,
  foot,
}: {
  label: string;
  value: ReactNode;
  foot?: ReactNode;
}) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {foot ? <div className="kpi-foot">{foot}</div> : null}
    </div>
  );
}

/** The eight-stage rail from the prototype, driven by the shared state machine. */
export function PipelineRail({ status }: { status: DealStatus }) {
  const meta = STATUS_META[status];
  return (
    <div className="rail">
      {PIPELINE_STAGES.map((label, i) => {
        const state =
          i < meta.stage ? 'done' : i === meta.stage ? (meta.offRamp ? 'off' : 'current') : '';
        return (
          <div key={label} className={`rail-step ${state}`}>
            <div
              className="rail-dot"
              style={
                // An off-ramp (disputed, overdue) sits at its stage but must not
                // read as forward progress.
                state === 'off'
                  ? { background: 'var(--rose)', boxShadow: '0 0 0 3px var(--rose-soft)' }
                  : undefined
              }
            />
            <div className="rail-label">{label}</div>
          </div>
        );
      })}
    </div>
  );
}

export function Card({
  title,
  sub,
  actions,
  children,
  padded = true,
}: {
  title?: ReactNode;
  /** A quiet second line saying what the figure below is measured over. */
  sub?: string;
  actions?: ReactNode;
  children: ReactNode;
  padded?: boolean;
}) {
  return (
    <div className="card">
      {title ? (
        <div className="card-head">
          {/* Stacked rather than side by side: a title long enough to wrap
              would otherwise squeeze the subtitle into a column of one word
              each. */}
          <div>
            <div className="card-title">{title}</div>
            {sub ? (
              <div className="cell-t2" style={{ marginTop: 2 }}>
                {sub}
              </div>
            ) : null}
          </div>
          <div style={{ marginLeft: 'auto' }}>{actions}</div>
        </div>
      ) : null}
      <div className={padded ? 'card-body' : ''}>{children}</div>
    </div>
  );
}

export function PageHead({
  crumb,
  title,
  sub,
  actions,
}: {
  crumb: string;
  title: string;
  sub?: string;
  actions?: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 4 }}>
      <div style={{ flex: 1 }}>
        <div className="crumb">{crumb}</div>
        <h1 className="page-title">{title}</h1>
        {sub ? <div className="page-sub">{sub}</div> : null}
      </div>
      {actions ? <div style={{ paddingBottom: 22 }}>{actions}</div> : null}
    </div>
  );
}

export function EmptyState({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="empty">
      <div style={{ fontWeight: 600, color: 'var(--muted)', marginBottom: 4 }}>{title}</div>
      {sub ? <div style={{ fontSize: 12.5 }}>{sub}</div> : null}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  return <div className="err">{message}</div>;
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return <div className="empty">{label}…</div>;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Days until a date; negative once it is in the past. */
export function daysUntil(iso: string): number {
  return Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);
}
